import { useEffect, useRef } from 'react';

/**
 * useBackButtonClose — Mobil/tablet hardware "geri" tuşunda modal kapatma.
 *
 * Modal açıldığında history stack'e bir entry push eder (`{ modalOpen: true }`).
 * Kullanıcı browser/Android back butonuna bastığında popstate yakalanır ve
 * `onClose` callback'i çağrılır → modal kapanır. Sayfa navigate olmaz.
 *
 * Modal kullanıcı tarafından (X butonu, ESC, backdrop click) kapatıldığında ise
 * cleanup fazında history.replaceState ile fake entry temizlenir, böylece
 * "geri" butonu beklenen sayfaya gider.
 *
 * Kullanım:
 *   useBackButtonClose(showModal, () => setShowModal(false));
 *
 * `onClose` referansı her render değişse de etki tetiklenmez — internal ref
 * ile en güncel callback'e erişilir; effect sadece `open` değiştiğinde re-run.
 *
 * @param {boolean} open Modal açık mı?
 * @param {() => void} onClose Modal'ı kapatma callback'i
 */
export default function useBackButtonClose(open, onClose) {
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === 'undefined') return undefined;

    let pushed = false;
    try {
      window.history.pushState({ modalOpen: true }, '');
      pushed = true;
    } catch (_) {
      // sandbox / SSR fallback
    }

    const onPop = () => {
      // Bu noktada browser zaten fake entry'yi pop etti; sadece modal'ı kapat
      pushed = false;
      try {
        if (typeof onCloseRef.current === 'function') onCloseRef.current();
      } catch (_) { /* no-op */ }
    };

    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      // Modal user-action ile kapandıysa, push ettiğimiz fake entry hâlâ history top'unda
      // → replaceState ile temizle ki kullanıcı sonradan "geri"ye basınca beklediği sayfaya gitsin
      if (pushed && window.history.state && window.history.state.modalOpen) {
        try { window.history.replaceState(null, ''); } catch (_) { /* no-op */ }
      }
    };
    // onClose intentionally excluded — onCloseRef always reads the latest value,
    // and re-running the effect on every render would re-pushState (broken behavior).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
